import type { AskRevFilter } from '@/types/askRevFilters';
import { usePropertyContext } from 'context/propertyContext';
import dayjs from 'dayjs';
import useApolloErrorAlerts from 'features/alerts/hooks/use-apollo-error-alerts';
import {
	addDays,
	dataDate,
	dateFormatData,
	dayRange,
	defaultPriorDays,
	getDayOfWeek,
	isDuringDataProcessing,
	today,
	todaySubtract,
} from 'features/dates/date-helpers';
import { useGetEventsQuery } from 'features/events/gql/_gen_/events.gql';
import {
	selectEnableSellOutFlag,
	selectShowAskRev,
} from 'features/header/redux/selectors';
import {
	useGetDailyRateRecsQuery,
	useGetLiveDataQuery,
	useGetPropRoomTypesQuery,
	useGetRateShopQuery,
	useGetRoomTypeRecRatesQuery,
	useGetUserFavNightsQuery,
} from 'features/my-view/gql/_gen_/rec-review.gql';
import { setShowDataProcessingMessage } from 'features/my-view/redux/my-view-slice';
import { selectQueryInput } from 'features/my-view/redux/selectors';
import {
	useGetIntradayForecastQuery,
	useGetPropertyRecRatesQuery,
} from 'features/rec-rates/gql/_gen_/property-rec-rates.gql';
import { REC_STATUS_ALL } from 'features/rec-rates/rec-rate-helpers';
import { DOW_ALL } from 'features/search/search-helpers';
import { useUser } from 'features/users/context/userContext';
import type {
	Competitor,
	EventResponse,
	LiveData,
	PropCompetitor,
	PropRecReview,
	RateChange,
	RateObj,
	RecReview,
	SearchInfo,
	SearchMetricInfo,
} from 'graphql/gql-types';
import { toFixedNumber } from 'helpers/math-helpers';
import { getSeasonFormatData, useRecReview } from 'hooks/useRecReview';
import { labels } from 'locales/en.label';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import type { SeasonType } from 'types/RecReviewTypes';
import type { Maybe } from 'types/maybe-type';
import { replaceVariablesInFilters } from 'utils/askrev/variable-replacer';
import type { RoomTypeRecRate } from '../../graphql/gql-types';
import { calcTotalForecast } from './tableCustomCellRender';

export type DashboardControllerProps = {
	propertyId?: string;
	askRevFilters: AskRevFilter[];
	// NEW: AI-suggested sorting parameters
	aiSortBy?: string | null;
	aiSortDirection?: 'asc' | 'desc' | null;
	aiSortLimit?: number | null;
};
export type FavClickParams = {
	data: { property_id: string; stay_date: string };
};
export interface CompRateObj
	extends Partial<
		Omit<RateObj, 'hotelId' | 'arrivalDate' | 'extractDateTime' | 'changes'>
	> {
	hotelId: number;
	arrivalDate: string;
	extractDateTime: string;
	changes?: Pick<RateChange, 'day' | 'value'>[];
}

export type RatesById = {
	[id: number]: CompRateObj;
	market_rate_live?: number;
	one_day?: number;
	three_day?: number;
	seven_day?: number;
};
export type RecReviewData = Omit<
	PropRecReview & LiveData & { compRates: RatesById; season: SeasonType },
	'__typename' | 'last_updated_date'
> &
	Omit<RecReview, '__typename'> & {
		__typename?: string;
		last_updated_by?: string | null | undefined;
		event_count?: number;
		events?: EventResponse[];
		roomTypeRates?: RoomTypeRecRate[] | null;
		// Restriction fields for the new column
		restriction_code?: string | null;
		restriction_type?: string | null;
		restriction_applies_all_rooms?: boolean | null;
		has_restriction_recommendation?: boolean | null;
		restriction_details?: string | null;
		conservative_rate?: number;
		neutral_rate?: number;
		aggressive_rate?: number;
	};

export interface DashboardControllerResult {
	data: RecReviewData[];
	compSet: Pick<Competitor, 'hotelId' | 'name'>[];
	loading: boolean;
	missingStatusMap: Set<string>;
	refetchRateShop: (variables?: Partial<any> | undefined) => Promise<any>;
	roomTypes?: Array<{ room_category: string; category_code: string }>;
	roomTypeRates?: RoomTypeRecRate[] | null;
	// NEW: AI sorting parameters to be applied at AG-Grid level
	aiSorting?: {
		sortBy: string | null;
		sortDirection: 'asc' | 'desc' | null;
		sortLimit: number | null;
	} | null;
}

const getFormattedStayDate = (o: { stay_date?: string }) =>
	dataDate(o?.stay_date);
export const filterByAskRevQuery = (
	rowData: RecReviewData[],
	askRevFilters: AskRevFilter[] | undefined,
): RecReviewData[] => {
	if (askRevFilters && askRevFilters.length > 0) {
		// First, resolve any variable expressions in the filters
		const resolvedFilters = replaceVariablesInFilters(askRevFilters);

		// Group filters by field name
		const filtersByField = resolvedFilters.reduce(
			(groups, filter) => {
				const field = filter.field;
				if (!groups[field]) {
					groups[field] = [];
				}
				groups[field].push(filter);
				return groups;
			},
			{} as Record<string, AskRevFilter[]>,
		);
		const filteredRows = rowData.filter((row) => {
			// For each field group, apply appropriate logic within the group
			// and AND logic between different field groups
			return Object.entries(filtersByField).every(([, filters]) => {
				// For range queries (multiple filters on same field), use AND logic
				// For multiple values on same field (e.g., different statuses), use OR logic
				// We determine this by checking if we have range operators (>, <, >=, <=)
				const hasRangeOperators = filters.some((filter) =>
					['>', '<', '>=', '<='].includes(filter.operator),
				);
				if (hasRangeOperators) {
					// AND logic for range filters - all conditions must pass
					return filters.every((filter) => {
						const compareValue = getCompareValue(row, filter.value);
						const fieldValue = getFieldValue(row, filter.field, filter.value);
						return compare(fieldValue, filter.operator, compareValue);
					});
				}
				// OR logic for equality filters - at least one condition must pass
				return filters.some((filter) => {
					const compareValue = getCompareValue(row, filter.value);
					const fieldValue = getFieldValue(row, filter.field, filter.value);
					return compare(fieldValue, filter.operator, compareValue);
				});
			});
		});
		return filteredRows;
	}
	return rowData;
};
const compareFunctions: { [operator: string]: (a: any, b: any) => boolean } = {
	'>': (a, b) => a > b,
	'<': (a, b) => a < b,
	'>=': (a, b) => a >= b,
	'<=': (a, b) => a <= b,
	'!=': (a, b) => a !== b,
	'=': (a, b) => a === b,
	'===': (a, b) => a === b,
	'!==': (a, b) => a !== b,
};
const compare = (a: any, operator: string, b: any) => {
	const fn = compareFunctions[operator];
	if (!fn) return false;

	const normalizedA = normalizeValue(a);
	const normalizedB = normalizeValue(b);

	// Handle undefined values (e.g., when competitor rates are not available)
	if (normalizedA === undefined || normalizedB === undefined) {
		// For equality operators, undefined should only equal undefined
		if (operator === '===' || operator === '=') {
			return normalizedA === normalizedB;
		}
		if (operator === '!==' || operator === '!=') {
			return normalizedA !== normalizedB;
		}
		// For comparison operators, undefined values should not match
		return false;
	}

	return fn(normalizedA, normalizedB);
};
// Converts string inputs like "true", "123" to proper boolean/number
const normalizeValue = (val: any): any => {
	if (typeof val === 'string') {
		const trimmed = val.trim().toLowerCase();
		if (trimmed === 'true') return true;
		if (trimmed === 'false') return false;
		const num = Number(trimmed);
		if (!Number.isNaN(num)) return num;
	}
	return val;
};
// Helper function to get field value, handling competitor rates
const getFieldValue = (
	row: RecReviewData,
	field: string,
	compareValue?: any,
): any => {
	// Check if field is in compRate.{hotelId} or compRates.{hotelId} format
	const compRateMatch = field.match(/^comp(?:Rate|Rates)\.(\d+)$/);
	if (compRateMatch) {
		const hotelId = Number.parseInt(compRateMatch[1], 10);
		const compRateObj = row.compRates?.[hotelId];

		if (!compRateObj) {
			return undefined;
		}

		// Check if we're comparing against a rates.{message} pattern
		if (
			typeof compareValue === 'string' &&
			compareValue.match(/^rates\.(.+)$/)
		) {
			// Extract the expected message from rates.{message} format
			const expectedMessage = compareValue.replace('rates.', '');

			// Check for exact match first
			if (compRateObj.message === compareValue) {
				return compareValue; // Exact match
			}

			// Check for legacy compatibility - if message contains the expected pattern
			if (
				expectedMessage === 'soldout' &&
				(compRateObj.message?.toLowerCase().includes('soldout') ||
					compRateObj.message?.toLowerCase().includes('sellout') ||
					compRateObj.value === null ||
					compRateObj.value === undefined)
			) {
				return compareValue; // Return the expected format for comparison
			}

			// For other cases, return the actual message
			return compRateObj.message;
		}

		// Check if the competitor rate is soldout by examining the message field
		// Check for exact match of "rates.soldout" and also legacy patterns like "Sellout" for backward compatibility
		if (
			compRateObj.message === 'rates.soldout' ||
			compRateObj.message?.toLowerCase().includes('soldout') ||
			compRateObj.message?.toLowerCase().includes('sellout')
		) {
			return 'rates.soldout';
		}

		// Legacy behavior: if value is null/undefined, return 'rates.soldout'
		if (compRateObj.value === null || compRateObj.value === undefined) {
			return 'rates.soldout';
		}
		return compRateObj.value;
	}
	// Regular field access
	const rawValue = row[field as keyof RecReviewData];

	// Handle invalid values for field-to-field comparisons
	// Treat null, 0, and empty string as undefined to exclude from comparisons
	if (rawValue === null || rawValue === 0 || rawValue === '') {
		return undefined;
	}

	return rawValue;
};
// Helper function to get compare value, handling competitor rate references
const getCompareValue = (row: RecReviewData, value: any): any => {
	// Check if value is also a compRate.{hotelId} or compRates.{hotelId} reference
	if (typeof value === 'string') {
		const compRateMatch = value.match(/^comp(?:Rate|Rates)\.(\d+)$/);
		if (compRateMatch) {
			const hotelId = Number.parseInt(compRateMatch[1], 10);
			const compRateObj = row.compRates?.[hotelId];

			if (!compRateObj) {
				return undefined;
			}

			return compRateObj.value;
		}

		// Check if value is in rates.{message} format - return as-is for direct comparison
		if (value.match(/^rates\.(.+)$/)) {
			return value; // Return the full rates.{message} format for direct comparison
		}

		// NEW: Check if value is a field name reference (field-to-field comparison)
		// Check if the string is a property that exists in the row data
		if (value in row && row[value as keyof RecReviewData] !== undefined) {
			return getFieldValue(row, value);
		}
	}
	// Regular value
	return value;
};

const compSetDTO = (compRates?: Maybe<PropCompetitor>[]) => {
	const currentComps = compRates?.find((cs) => cs?.stay_date === today());
	if (!currentComps) return [];
	const compSet: Pick<Competitor, 'hotelId' | 'name'>[] = [];
	for (let i = 1; i <= 10; i++) {
		const hotelId = currentComps[
			`comp_id_${i}` as keyof PropCompetitor
		] as number;
		const name = currentComps[
			`comp_name_${i}` as keyof PropCompetitor
		] as string;
		if (hotelId) compSet.push({ hotelId, name });
	}
	return compSet;
};
const compRatesDTO = (compRates?: Maybe<PropCompetitor>[]) => {
	if (!compRates?.length) return {};
	const transformedRates: { [stayDate: string]: RatesById } = {};
	for (const dailyRates of compRates) {
		if (!dailyRates) continue;
		const ratesObj: RatesById = {};
		for (let i = 1; i <= 10; i++) {
			const hotelId = dailyRates[
				`comp_id_${i}` as keyof PropCompetitor
			] as number;
			const value = dailyRates[
				`comp_rate_${i}` as keyof PropCompetitor
			] as number;
			if (hotelId) {
				ratesObj[hotelId] = {
					hotelId,
					value,
					extractDateTime: dailyRates.last_updated_date!,
					arrivalDate: dailyRates.stay_date!,
				};
			}
		}
		transformedRates[dailyRates.stay_date!] = ratesObj;
	}
	return transformedRates;
};
const liveFields = new Set([
	'adr',
	'available_rooms',
	'current_otb',
	'current_rate',
	'current_revenue',
	'group_blocked',
	'group_booked',
	'occ_pct',
	'ooo',
	'revpar',
	'total_capacity',
	'transient_booked',
	'transient_capacity',
	'a_otb',
	'b_otb',
	'c_otb',
	'd_otb',
	'e_otb',
	'f_otb',
	'g_otb',
	'h_otb',
]);

// Interface moved to types.ts

export const useDashboardController = ({
	propertyId,
	askRevFilters,
	aiSortBy,
	aiSortDirection,
	aiSortLimit,
}: DashboardControllerProps): DashboardControllerResult => {
	const { user } = useUser();
	const { showPast } = usePropertyContext();
	const defaultStartDate = useMemo(
		() => (showPast ? todaySubtract(1, 'week') : today()),
		[showPast],
	);
	const daysPrior = showPast ? defaultPriorDays : 0;
	const [data, setData] = useState<RecReviewData[]>([]);
	const dispatch = useAppDispatch();
	const showAskRev = useAppSelector(selectShowAskRev);
	const enableSellOutFlag = useAppSelector(selectEnableSellOutFlag);
	// This is a search to power the AskBestREV feature, and is temporary.
	// TODO: We will build a new search for this feature in the next sprint.
	const defaultInput: SearchInfo = {
		userId: user?.id,
		duration: 365,
		selectedDays: DOW_ALL,
		startDate: defaultStartDate,
		statuses: REC_STATUS_ALL,
	};
	const queryInput = useAppSelector(selectQueryInput);
	const defaultEndDate = queryInput.duration
		? addDays(queryInput?.duration + daysPrior, defaultStartDate).format(
				dateFormatData,
			)
		: addDays(30 + daysPrior, defaultStartDate).format(dateFormatData);
	const {
		data: recReviewData,
		loading: loadingRecReview,
		error: recReviewError,
	} = useRecReview(defaultInput, propertyId);
	const {
		data: rateShopData,
		error: compSetError,
		loading: loadingRateShop,
		refetch: refetchRateShop,
	} = useGetRateShopQuery({
		skip: !propertyId,
		variables: { propertyId: propertyId! },
		notifyOnNetworkStatusChange: true,
	});
	const {
		data: recRatesData,
		loading: loadingRecRates,
		error: recRatesError,
	} = useGetPropertyRecRatesQuery({
		skip: !propertyId,
		variables: {
			propertyId,
			dateRange: {
				startDate: defaultStartDate,
				endDate: defaultEndDate,
			},
		},
	});
	const { data: intradayForecastData } = useGetIntradayForecastQuery({
		skip: !propertyId,
		variables: { propertyId: propertyId! },
	});
	const { data: favData, error: favDataError } = useGetUserFavNightsQuery({
		skip: !propertyId || !user?.id,
		variables: {
			propertyId,
			userId: user?.id,
		},
	});
	const { data: liveRatesData } = useGetLiveDataQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId!,
		},
	});

	const { data: eventsData } = useGetEventsQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId!,
		},
	});

	const { data: roomTypesData } = useGetPropRoomTypesQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId!,
		},
	});

	const { data: roomTypeRatesData } = useGetRoomTypeRecRatesQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId!,
			startDate: defaultStartDate,
			endDate: defaultEndDate,
			lastUpdatedDate: today(),
		},
	});

	const { data: dailyRateRecsData } = useGetDailyRateRecsQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId!,
			startDate: defaultStartDate,
			endDate: defaultEndDate,
		},
	});

	useEffect(() => {
		if (
			recReviewData?.PropMetrics?.results.length === 0 &&
			isDuringDataProcessing()
		) {
			dispatch(setShowDataProcessingMessage(true));
		} else {
			dispatch(setShowDataProcessingMessage(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recReviewData]);
	useApolloErrorAlerts([
		recReviewError,
		compSetError,
		recRatesError,
		favDataError,
	]);
	// Prepare a list of dates to use as the foundation for the data.
	const dateRangeList = useMemo(
		() =>
			dayRange(defaultStartDate, defaultEndDate).filter((day: string) => {
				const dow = getDayOfWeek(day);
				return queryInput?.selectedDays?.includes(DOW_ALL[dow]);
			}),
		[defaultStartDate, defaultEndDate, queryInput?.selectedDays],
	);
	const metricsByDate = useMemo(
		() =>
			_.keyBy(recReviewData?.PropMetrics?.results, (m) =>
				dataDate(m.stay_date),
			),
		[recReviewData?.PropMetrics?.results],
	);
	const compRatesByDate = useMemo(() => {
		const result: { [stayDate: string]: RatesById } = {};
		if (rateShopData?.getRateShop.dailyRates.length === 0) {
			return compRatesDTO(recReviewData?.PropCompetitorSet?.results);
		}
		for (const {
			stayDate,
			rates,
			marketRate,
			oneDay,
			threeDay,
			sevenDay,
		} of rateShopData?.getRateShop.dailyRates || []) {
			const ratesById: RatesById = {};
			for (const r of rates) {
				ratesById[r.hotelId] = r;
			}
			ratesById.market_rate_live = marketRate;
			ratesById.one_day = oneDay;
			ratesById.three_day = threeDay;
			ratesById.seven_day = sevenDay;
			result[stayDate] = ratesById;
		}
		return result;
	}, [
		rateShopData?.getRateShop.dailyRates,
		recReviewData?.PropCompetitorSet?.results,
	]);
	const rateLevelsByDate = useMemo(
		() =>
			_.keyBy(recReviewData?.getPropRateLevels, (rl) =>
				dataDate(rl?.stay_date),
			),
		[recReviewData?.getPropRateLevels],
	);
	const liveDataByDate = useMemo(
		() => _.keyBy(liveRatesData?.getLiveData, (lv) => dataDate(lv?.stay_date)),
		[liveRatesData?.getLiveData],
	);
	const recRatesByDate = useMemo(
		() =>
			_.keyBy(recRatesData?.getPropertyRecRates, (rr) =>
				dataDate(rr?.stay_date),
			),
		[recRatesData?.getPropertyRecRates],
	);
	const seasonsByDate = useMemo(() => {
		const seasonData = recReviewData?.getPropSeasons?.filter((season) => {
			return season && dayjs(season.season_end).isSameOrAfter(defaultStartDate);
		});
		return getSeasonFormatData(seasonData);
	}, [recReviewData?.getPropSeasons, defaultStartDate]);
	const favNightsByDate: { [stayDate: string]: boolean } = useMemo(
		() =>
			_.mapValues(
				_.keyBy(favData?.listUserFavNights, getFormattedStayDate),
				'is_favorite',
			),
		[favData?.listUserFavNights],
	);
	const intradayDataByDate = useMemo(
		() =>
			_.keyBy(intradayForecastData?.getIntradayForecast, (i) =>
				dataDate(i?.stay_date),
			),
		[intradayForecastData?.getIntradayForecast],
	);

	// Process events data to group by date
	const eventsByDate = useMemo(() => {
		const eventMap: { [stayDate: string]: EventResponse[] } = {};

		if (!eventsData?.getEvents) return eventMap;

		for (const event of eventsData.getEvents) {
			if (!event.start_date || !event.end_date) continue;

			const startDate = dayjs(event.start_date);
			const endDate = dayjs(event.end_date);

			// For each day in the event's date range, add the event
			let currentDate = startDate;
			while (currentDate.isSameOrBefore(endDate, 'day')) {
				const dateKey = currentDate.format(dateFormatData);
				if (!eventMap[dateKey]) {
					eventMap[dateKey] = [];
				}
				eventMap[dateKey].push(event);
				currentDate = currentDate.add(1, 'day');
			}
		}

		return eventMap;
	}, [eventsData?.getEvents]);

	const dailyRateRecsByDate = useMemo(
		() =>
			_.keyBy(dailyRateRecsData?.getDailyRateRecs, (dr) =>
				dataDate(dr?.stay_date),
			),
		[dailyRateRecsData?.getDailyRateRecs],
	);
	const compSet = useMemo(
		() =>
			rateShopData?.getRateShop.compSet.length
				? rateShopData?.getRateShop.compSet
				: compSetDTO(recReviewData?.PropCompetitorSet?.results),
		[
			rateShopData?.getRateShop.compSet,
			recReviewData?.PropCompetitorSet?.results,
		],
	);

	const roomTypes = useMemo(() => {
		if (!roomTypesData?.getPropRoomTypes) return undefined;
		const types = roomTypesData.getPropRoomTypes
			.map((roomType) => ({
				room_category: roomType?.room_category || '',
				category_code: roomType?.category_code || '',
			}))
			.filter(
				(roomType, index, self) =>
					index ===
					self.findIndex((t) => t.room_category === roomType.room_category),
			); // Remove duplicates
		return types;
	}, [roomTypesData]);

	// Process room type rates data
	const roomTypeRates = useMemo(() => {
		if (!roomTypeRatesData?.getRoomTypeRecRates) return null;
		// Filter out undefined values and ensure type safety
		const rates = roomTypeRatesData.getRoomTypeRecRates.filter(
			(rate): rate is RoomTypeRecRate => rate != null,
		);

		return rates;
	}, [roomTypeRatesData]);

	const missingStatusMap: Set<string> = new Set();
	useEffect(() => {
		const giveMeResult = (
			data: RecReviewData[],
			criteria: SearchMetricInfo,
		) => {
			const rhsCondition = criteria.rhsCode !== 'value';
			const lhs = criteria.lhsCode as keyof RecReviewData;
			const rhs = criteria.rhsCode as keyof RecReviewData;
			const getValue = (row: RecReviewData, field: keyof RecReviewData) => {
				const originalValue = row[field];
				if (liveFields.has(field)) {
					const liveName = `${lhs}_live` as keyof RecReviewData;
					const liveValue = row[liveName];
					return liveValue ?? originalValue;
				}
				return originalValue;
			};
			return data.filter((row) => {
				let leftValue = getValue(row, lhs);
				if (lhs === 'occ_pct' && typeof leftValue === 'number')
					leftValue = toFixedNumber(leftValue, 1);
				if (lhs === 'fcst_occ_pct')
					leftValue = toFixedNumber(
						calcTotalForecast(row)?.fcst_occ_pct ?? 0,
						1,
					);
				const rightValue = rhsCondition
					? getValue(row, rhs)
					: criteria.rhsCustomValue;
				if (leftValue === undefined || rightValue === undefined) return false;
				return compare(leftValue, criteria.operatorCode, rightValue);
			});
		};
		// filterBySearchCriteria gets called recursively until queryInput.searchCriteria.length //
		const filterBySearchCriteria: any = (
			data: RecReviewData[],
			start: number,
			length: number,
		) => {
			const result = giveMeResult(data, queryInput?.searchCriteria![start]!);
			if (length === 1) return result;
			if (length !== 0 && start !== length) {
				return filterBySearchCriteria(result, start + 1, length - 1);
			}
		};
		const formatDataForAgGridTable = () => {
			let rowData: RecReviewData[] =
				// Since we cannot guarantee that PropMetrics will not be length of zero, we should not use
				// it as the foundation for combining all the data. In RM Workspace we generate a list of dates and then
				// add the data from there. This allows us to show any data we do have.
				dateRangeList?.map((stay_date) => {
					const stayDate = dataDate(stay_date);
					const isPast = dayjs(stayDate).isBefore(dayjs(), 'day');
					const metrics = metricsByDate[stayDate];
					const rateLevels = rateLevelsByDate[stayDate];
					const liveData = liveDataByDate[stayDate];
					const recRates = isPast
						? _.omit(recRatesByDate[stayDate], 'rec_status')
						: recRatesByDate[stayDate];
					const compRates = compRatesByDate[stayDate];
					const season = seasonsByDate[stayDate];
					const is_favorite = favNightsByDate[stayDate] ?? false;
					const intraday = intradayDataByDate[stayDate];
					const dailyRateRecs = dailyRateRecsByDate[stayDate];
					const dayEvents = eventsByDate[stayDate] || [];
					const result: RecReviewData = {
						...metrics,
						...intraday, // Move intraday before rateLevels to avoid overwriting clean status data
						...rateLevels, // Prioritize clean rateLevels data (contains proper a_status values)
						...liveData,
						...recRates,
						compRates,
						season,
						is_favorite,
						stay_date,
						rec_rate: recRates?.rec_rate,
						roomTypeRates,
						conservative_rate: dailyRateRecs?.conservative_rate,
						neutral_rate: dailyRateRecs?.neutral_rate,
						aggressive_rate: dailyRateRecs?.aggressive_rate,
						event_count: dayEvents.length,
						events: dayEvents,
					};

					return result;
				}) || [];
			// if AskRev filters are applied, filter the data here
			if (askRevFilters.length > 0) {
				rowData = filterByAskRevQuery(
					rowData,
					askRevFilters
						? Array.isArray(askRevFilters)
							? askRevFilters
							: [askRevFilters]
						: undefined,
				);
			}
			if (
				enableSellOutFlag ||
				queryInput.searchName === labels.rec_review.search.search_names.alert
			) {
				rowData = rowData.filter((r) => r.sellout_ind === true);
			}
			if (queryInput?.statuses?.length! > 0 && !loadingRecRates) {
				rowData = rowData.filter((r) => {
					const stayDate = new Date(r.stay_date);
					const today = new Date();
					if (!r.rec_status) {
						if (stayDate > today) {
							missingStatusMap.add(r.stay_date);
							// console.log('Adding missing map for ', missingStatusMap.size);
						}
						return true;
					}
					if (stayDate > today) {
						missingStatusMap.delete(r.stay_date);
						// console.log('Removing missing map for ', missingStatusMap.size);
					}
					return queryInput.statuses?.includes(r.rec_status);
				});
			}
			if (queryInput?.searchCriteria?.length! > 0) {
				const filterDataBySearchCriteria: RecReviewData[] =
					rowData &&
					filterBySearchCriteria(
						rowData,
						0,
						queryInput.searchCriteria?.length!,
					)!;
				rowData = filterDataBySearchCriteria;
			}

			return rowData || [];
		};
		const data = formatDataForAgGridTable();

		setData(data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		askRevFilters,
		compRatesByDate,
		dailyRateRecsByDate,
		dateRangeList,
		enableSellOutFlag,
		eventsByDate,
		favNightsByDate,
		intradayForecastData?.getIntradayForecast,
		intradayDataByDate,
		liveDataByDate,
		loadingRecRates,
		metricsByDate,
		queryInput.searchCriteria,
		queryInput.searchName,
		queryInput.statuses,
		rateLevelsByDate,
		recRatesByDate,
		roomTypeRates,
		seasonsByDate,
		showAskRev,
		defaultStartDate,
		// NEW: AI sorting parameters
		aiSortBy,
		aiSortDirection,
		aiSortLimit,
	]);
	return {
		data,
		compSet,
		loading: loadingRecReview || loadingRecRates || loadingRateShop,
		missingStatusMap,
		refetchRateShop,
		roomTypes,
		roomTypeRates,
		// Return AI sorting parameters for AG-Grid application
		aiSorting:
			aiSortBy || aiSortDirection || aiSortLimit
				? {
						sortBy: aiSortBy || null,
						sortDirection: aiSortDirection || null,
						sortLimit: aiSortLimit || null,
					}
				: null,
	};
};
